import React, { useCallback, useMemo, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { CalendarDays, Clock, List, Workflow } from 'lucide-react';
import { DATASET_TRIGGER_CRITERION_LABELS } from '@cotera/sdk/core';
import { useWhoami } from '@cotera/client/app/stores/org';
import { Text } from '@cotera/client/app/components/ui';
import { cn } from '@cotera/client/v0/lib/utils';
import { RESOURCE_LIST_CELL_TEXT } from '../resources/list/resource-list-types';
import {
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { CocoEvent, useTracking } from '@cotera/client/app/etc';
import {
  ResourceListView,
  ResourceListSecondaryCells,
  type ItemAction,
  type Column,
} from '../resources/list/resource-list-view';
import { ResourceMenuItem } from '../resources/list/resource-item-actions';
import { FilterChipGroup } from '../resources/list/filter-chip-group';
import { ListFilterMenu } from '../resources/list/list-filter-menu';
import { useIsBelowSm } from '@cotera/client/app/hooks/use-breakpoint';
import { useFavoriteMenuItem } from '../resources/favorites';
import {
  LIST_ROW_ACTIONS_CELL,
  LIST_ROW_CELL,
  LIST_ROW_STATUS_CELL,
  LIST_ROW_STATUS_WIDTH,
  LIST_ROW_TIME_WIDTH,
} from '../workflow/workflow-list-row';
import {
  TriggersListModel,
  useTriggersListModel,
  type TriggerCategoryFilter,
  type TriggerListItem,
  type TriggersViewMode,
} from './triggers-list.model';
import { ResourceType } from '../resources/selection/resource-selection.model';
import { ResourceTypeIconBubble } from '../resources/type/resource-type-icon-bubble';
import {
  formatNextRun,
  getNextRun,
  humanReadableSchedule,
} from './schedule-utils';
import { TriggersCalendar } from './triggers-calendar';
import { MoveToFolderAction } from '../resources/actions/move-to-folder.action';
import { ArchiveResourceAction } from '../resources/actions/archive-resource.action';
import { ShareResourceLinkAction } from '../resources/actions/share-resource-link.action';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@cotera/client/v0/components/ui/toggle-group';
import {
  ListPageShell,
  useListPageModelFactory,
} from '../resources/list-page-shell';
import { useResourceListSelection } from '../resources/selection/use-resource-list-selection';
import {
  TriggersAccordionView,
  type WorkflowRowRef,
} from './triggers-accordion';
import { useOpenNewTriggerModal } from './use-open-new-trigger-modal';
import {
  useDatasetOptions,
  useTriggerSummaries,
  useWorkflowsOverview,
} from './hooks';
import {
  TRIGGER_HEALTH_LABEL,
  matchesHealthFilter,
  type TriggerHealth,
} from './trigger-health';
import { NewWorkflowAction } from '../workflow/new-workflow.action';
import { RenameWorkflowAction } from '../workflow/rename-workflow.action';

/**
 * The right-hand columns the trigger rows and their nested workflow rows both
 * line up under. Widths come from the row-geometry constants so the rows, this
 * declaration and `AutomationsColumnHeader` can't drift apart.
 *
 * `ResourceListView` only renders these itself for the loading skeleton — once
 * data is in, the grouped accordion is a `contentOverride` and the header below
 * takes over (a `<table>` header can't line up with the accordion's flex rows).
 */
const TRIGGER_COLUMNS: Column[] = [
  { label: 'Name' },
  {
    columnKey: 'status',
    label: 'Status',
    width: LIST_ROW_STATUS_WIDTH,
  },
  {
    columnKey: 'last-run',
    label: 'Last run',
    width: LIST_ROW_TIME_WIDTH,
  },
  {
    columnKey: 'next-run',
    label: 'Next run',
    width: LIST_ROW_TIME_WIDTH,
  },
];

/**
 * Column header for the grouped list, built from the same geometry constants the
 * rows use. Sticky by way of `ResourceListView`'s header band, which is where
 * `headerExtras` renders.
 */
const AutomationsColumnHeader: React.FC = () => (
  // `hidden md:flex`: the three columns this heads are themselves `md:block`
  // and `lg:block` (see `LIST_ROW_CELL`), so below `md` the row was a header
  // labelling one column — "Name", over a list of names — and a rule. That is a
  // row of a phone's viewport spent saying nothing.
  <div className="hidden w-full min-w-0 items-center border-b border-border pb-2 text-xs text-muted-foreground md:flex">
    {/*
     * "Name" sits at the left edge, not above the row names. Every other list
     * page heads this column from the same place — the label spans the caret and
     * icon columns rather than starting after them — so the header row reads the
     * same on all of them. Only the label moves: the span still takes the same
     * `flex-1` track, so the columns to its right stay put.
     */}
    <span className="min-w-0 flex-1 pl-3">Name</span>
    <span className={LIST_ROW_STATUS_CELL}>Status</span>
    <span className={LIST_ROW_CELL}>Last run</span>
    <span className={LIST_ROW_CELL}>Next run</span>
    {/* Matches the rows' trailing `…` slot; unlabelled, since the header can't
        name a control that only exists on hover. */}
    <span className={LIST_ROW_ACTIONS_CELL} aria-hidden="true" />
  </div>
);

/** Square icon slots matching the header's `+` button. */
const VIEW_TOGGLE_ITEM_CLASS = 'h-8 w-8 rounded-sm p-0';

/** Single-select header chips over the model's multi-select category filter. */
const CATEGORY_CHIPS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'dataset', label: 'Dataset' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'event', label: 'Event' },
  { value: 'standalone', label: 'Standalone' },
] as const;

type CategoryChip = (typeof CATEGORY_CHIPS)[number]['value'];

const NO_CATEGORY: TriggerCategoryFilter = {
  scheduled: false,
  webhook: false,
  event: false,
  dataset: false,
  standalone: false,
};

/** "A new row is added" — what a dataset trigger watches for, for search + subtitle. */
const datasetCriterionLabel = (trigger: TriggerListItem): string =>
  trigger.datasetCriterion !== null
    ? DATASET_TRIGGER_CRITERION_LABELS[trigger.datasetCriterion]
    : '';

const filterTrigger = (trigger: TriggerListItem, query: string): boolean => {
  const name = trigger.name.toLowerCase();
  const schedule = humanReadableSchedule(trigger.schedule).toLowerCase();
  const agentName = (trigger.agentTitle ?? '').toLowerCase();
  const criterion = datasetCriterionLabel(trigger).toLowerCase();
  return (
    name.includes(query) ||
    schedule.includes(query) ||
    agentName.includes(query) ||
    (criterion !== '' && criterion.includes(query))
  );
};

const TriggersListContent: React.FC = () => {
  const org = useWhoami((s) => s.org);
  const [searchParams] = useSearchParams();
  const triggersModel = useTriggersListModel();
  const moveToFolderAction = useAction(MoveToFolderAction);
  const archiveAction = useAction(ArchiveResourceAction);
  const shareAction = useAction(ShareResourceLinkAction);
  const renameWorkflowAction = useAction(RenameWorkflowAction);
  const navigate = useNavigate();
  const { handleOpenActionMenu, handleFocusItems } = useResourceListSelection();

  useTracking(CocoEvent.TriggersListViewed, { orgId: org.id });

  React.useEffect(() => {
    triggersModel.syncFromUrl(searchParams);
  }, [triggersModel, searchParams]);

  const listParams = useWatchableValue(triggersModel.listParamsWatchable);
  const { page, limit, search, view: viewMode, category } = listParams;
  const triggersData = useWatchableValue(triggersModel.triggersDataWatchable);

  const triggers = useMemo(() => triggersData?.items ?? [], [triggersData]);
  const total = triggersData?.total ?? 0;

  // Dataset triggers only carry the watched dataset's id, so resolve names for
  // their subtitle and for search. Only fetched when there's a dataset trigger
  // on the page — orgs without one shouldn't pay for the dataset list.
  const hasDatasetTriggers = useMemo(
    () => triggers.some((t) => t.kind === 'dataset'),
    [triggers]
  );
  /*
   * Rollups for every trigger on the page, not just the visible ones: the id set
   * stays stable as the chips and search narrow, so filtering doesn't refetch.
   * They live here rather than in the accordion because the health filter reads
   * them, and a filter can't narrow on data owned by what it filters.
   */
  const triggerSummaryIds = useMemo(
    () => triggers.map((t) => t.id),
    [triggers]
  );
  const { summaries: triggerSummaries, loaded: triggerSummariesLoaded } =
    useTriggerSummaries(triggerSummaryIds);

  const [healthFilter, setHealthFilter] = useState<ReadonlySet<TriggerHealth>>(
    () => new Set()
  );
  const toggleHealth = useCallback((health: TriggerHealth) => {
    setHealthFilter((prev) => {
      const next = new Set(prev);
      if (next.has(health)) {
        next.delete(health);
      } else {
        next.add(health);
      }
      return next;
    });
  }, []);
  const healthFilterActive = healthFilter.size > 0;
  /*
   * Triggers surviving the health filter. Counted off the unsorted, unsearched
   * list so the subtitle can report it too — a status filter that narrows the
   * rows while the count still claims the whole org reads as a broken filter
   * rather than an applied one.
   *
   * `total` is the server's count for the active chip, which is the right number
   * whenever no status is picked: it isn't capped by the page size.
   */
  const healthFilteredTotal = useMemo(
    () =>
      healthFilterActive
        ? triggers.filter((t) =>
            matchesHealthFilter(healthFilter, triggerSummaries.get(t.id))
          ).length
        : total,
    [healthFilterActive, healthFilter, triggers, triggerSummaries, total]
  );

  const datasetOptions = useDatasetOptions(hasDatasetTriggers);
  const datasetNameById = useMemo(
    () => new Map(datasetOptions.map((d) => [d.id, d.name])),
    [datasetOptions]
  );

  // Shared workflow rollup: powers the summary line and the "Standalone"
  // section (workflows with no trigger) in the accordion below.
  const {
    workflows: allWorkflows,
    summaries: workflowSummaries,
    unattached,
    capped: workflowsCapped,
    unattachedCapped,
  } = useWorkflowsOverview();

  // Workflows whose last run failed. The one count on this page that asks the
  // reader to do something, so it earns a place in the header rather than only
  // showing up as a red dot somewhere down the list.
  const failingCount = useMemo(
    () =>
      [...workflowSummaries.values()].filter(
        (summary) => summary.lastRunStatus === 'failed'
      ).length,
    [workflowSummaries]
  );

  // "15 workflows · 5 triggers · 2 standalone · 1 failing" — page health at a
  // glance, led by the workflows the page is named for. Workflow-derived counts
  // are a lower bound past the page cap, so render them as "N+" rather than
  // silently undercounting.
  //
  // Under a chip the line describes only what that chip shows. Of these four
  // numbers only the trigger total narrows with the filter (server-side, see
  // `categoryToTriggerKind`) — the workflow and standalone counts are org-wide
  // fetches with no category parameter — so the full breakdown put one filtered
  // number beside three unfiltered ones. Worse on "Standalone", which maps to no
  // a trigger kind at all, so the query stays unnarrowed and the line reported
  // every trigger in the org while showing none of them.
  const summaryLine = useMemo(() => {
    const activeKind = category.scheduled
      ? 'scheduled'
      : category.dataset
      ? 'dataset'
      : category.webhook
      ? 'webhook'
      : category.event
      ? 'event'
      : null;
    if (activeKind !== null) {
      return `${healthFilteredTotal} ${activeKind} trigger${
        healthFilteredTotal === 1 ? '' : 's'
      }`;
    }
    if (category.standalone) {
      const standalonePlus = unattachedCapped ? '+' : '';
      return `${unattached.length}${standalonePlus} standalone workflow${
        unattached.length === 1 && !unattachedCapped ? '' : 's'
      }`;
    }
    const plus = workflowsCapped ? '+' : '';
    const parts = [
      `${allWorkflows.length}${plus} workflow${
        allWorkflows.length === 1 && !workflowsCapped ? '' : 's'
      }`,
      `${healthFilteredTotal} trigger${healthFilteredTotal === 1 ? '' : 's'}`,
    ];
    if (unattached.length > 0) {
      parts.push(
        `${unattached.length}${unattachedCapped ? '+' : ''} standalone`
      );
    }
    if (failingCount > 0) {
      parts.push(`${failingCount} failing`);
    }
    return parts.join(' · ');
  }, [
    category,
    healthFilteredTotal,
    allWorkflows.length,
    unattached.length,
    workflowsCapped,
    unattachedCapped,
    failingCount,
  ]);

  // Category filter, rendered as the Library's header chips: "All" plus one
  // chip per source. Exactly one is ever active, which also lets the model
  // narrow the query server-side — a page-limited client filter would
  // under-report past 100. "Standalone" = workflows with no trigger.
  const activeCategoryChip: CategoryChip | 'all' = category.scheduled
    ? 'scheduled'
    : category.dataset
    ? 'dataset'
    : category.webhook
    ? 'webhook'
    : category.event
    ? 'event'
    : category.standalone
    ? 'standalone'
    : 'all';

  const handleCategoryChipChange = useCallback(
    (value: string) => {
      if (value === 'all' || value === '') {
        triggersModel.setCategory(NO_CATEGORY);
        return;
      }
      const chip = CATEGORY_CHIPS.find((c) => c.value === value);
      if (chip === undefined) {
        return;
      }
      triggersModel.setCategory({ ...NO_CATEGORY, [chip.value]: true });
    },
    [triggersModel]
  );
  const visibleCategories = useMemo(() => {
    const anyChecked =
      category.scheduled ||
      category.webhook ||
      category.event ||
      category.dataset ||
      category.standalone;
    return {
      scheduled: !anyChecked || category.scheduled,
      webhook: !anyChecked || category.webhook,
      event: !anyChecked || category.event,
      dataset: !anyChecked || category.dataset,
      // Standalone workflows are part of "All" like any other source. Valid
      // (run from Coco, the API, or another workflow) and they're the only place
      // you can notice a workflow you *meant* to automate and never wired up —
      // which this page itself causes when you archive a trigger. They live in a
      // collapsed group at the foot of the list, so being in "All" costs one row.
      // Hidden while a health filter is on: these are workflows with no trigger,
      // so they have no trigger health to match and would otherwise survive a
      // filter they were never tested against.
      standalone: (!anyChecked || category.standalone) && !healthFilterActive,
    };
  }, [category, healthFilterActive]);
  const hasMore = total > limit * page;
  const triggersStatus = triggersData === null ? 'loading' : 'ready';

  const handleLoadPage = useCallback(
    (page: number, take: number) => {
      triggersModel.loadPage(page, take);
    },
    [triggersModel]
  );

  const handleSearchChange = useCallback(
    (value: string) => triggersModel.setSearch(value),
    [triggersModel]
  );
  // The header's two shapes. See `ListFilterMenu`.
  const isNarrow = useIsBelowSm();

  const handleViewModeChange = useCallback(
    (v: TriggersViewMode) => triggersModel.setViewMode(v),
    [triggersModel]
  );

  const openNewTriggerModal = useOpenNewTriggerModal();
  // The list refetches itself off the ResourceUpdated broadcast the creation
  // hook emits, so no explicit refresh is needed here.
  const handleNewTrigger = useCallback(
    () => openNewTriggerModal(),
    [openNewTriggerModal]
  );

  // Net-new workflow (no seed trigger) — the page-level counterpart to the
  // per-trigger "Create new workflow" in the accordion.
  const newWorkflowAction = useAction(NewWorkflowAction);
  const handleNewWorkflow = useCallback(
    () => void newWorkflowAction.execute({}),
    [newWorkflowAction]
  );

  // The same colour-coded tile the rows below use, so what you create is
  // recognisable before it exists. The `size-`/`text-` prefixes are deliberate,
  // as in the Library's "New" menu: `DropdownMenuItem` styles bare descendant
  // svgs, and those selectors outrank a plain utility — without the prefixes the
  // glyph renders 16px and muted grey.
  const primaryAction = useMemo(
    () => ({
      label: 'New',
      onClick: handleNewWorkflow,
      menuItems: [
        {
          label: 'Workflow',
          icon: (
            <ResourceTypeIconBubble
              resourceType={ResourceType.Workflow}
              icon={Workflow}
              className="h-6 w-6 rounded"
              iconClassName="size-3.5 text-inherit"
            />
          ),
          onClick: handleNewWorkflow,
        },
        {
          label: 'Trigger',
          icon: (
            <ResourceTypeIconBubble
              resourceType={ResourceType.WorkflowTrigger}
              icon={Clock}
              className="h-6 w-6 rounded"
              iconClassName="size-3.5 text-inherit"
            />
          ),
          onClick: handleNewTrigger,
        },
      ],
    }),
    [handleNewWorkflow, handleNewTrigger]
  );

  // Opens the workflow the trigger runs, which is where every other row on this
  // page goes (see `WorkflowRow`, and the new-trigger wizard's post-create
  // navigation). It used to go to `/triggers/:id` — a route whose param is named
  // `triggerId` but is read as a workflow id, and which loads a *scheduled-agent
  // bundle*. Anything that isn't one of those legacy bundles left the page
  // rendering "Loading..." forever, because its guard treats an unresolved
  // workflow the same as one still in flight.
  /*
   * Creates a workflow already bound to this trigger and opens its builder. This
   * is the capability that went away with the per-group "Attach workflow" row:
   * a trigger that ends up with zero workflows — its only workflow archived, or
   * its binding removed — had no route back, since the trigger row itself is the
   * accordion toggle and can't also navigate.
   */
  const handleAddWorkflowToTrigger = useCallback(
    (trigger: TriggerListItem) => {
      void newWorkflowAction.execute({
        seedTrigger: {
          id: trigger.id,
          kind: trigger.kind,
          name: trigger.name,
          schedule: trigger.schedule,
          eventName: trigger.eventName,
        },
      });
    },
    [newWorkflowAction]
  );

  const handleClickTrigger = useCallback(
    (trigger: TriggerListItem) => {
      if (trigger.workflowId !== null && trigger.workflowId !== '') {
        navigate(`/${org.slug}/workflows/${trigger.workflowId}`);
      }
    },
    [navigate, org.slug]
  );

  const handleOpenMenu = useCallback(
    (trigger: TriggerListItem) =>
      handleOpenActionMenu({
        id: trigger.id,
        name: trigger.name,
        resourceType: ResourceType.WorkflowTrigger,
      }),
    [handleOpenActionMenu]
  );

  const handleFocusTriggers = useCallback(
    (focusedTriggers: TriggerListItem[]) =>
      handleFocusItems(
        focusedTriggers.map((trigger) => ({
          id: trigger.id,
          name: trigger.name,
          resourceType: ResourceType.WorkflowTrigger,
        }))
      ),
    [handleFocusItems]
  );

  const handleArchiveTriggers = useCallback(
    (selectedTriggers: TriggerListItem[]) => {
      handleFocusTriggers(selectedTriggers);
      void archiveAction.execute({});
    },
    [archiveAction, handleFocusTriggers]
  );

  const favoriteMenuItem = useFavoriteMenuItem();

  const getTriggerActions = useCallback(
    (trigger: TriggerListItem, workflowCount = 0): ItemAction[] => {
      // Archiving a trigger leaves its bound workflows behind (they're shared,
      // reusable resources) — warn so it's clear the workflows survive but stop
      // running from this trigger.
      const archiveConfirmSubtitle =
        workflowCount > 0
          ? `This trigger has ${workflowCount} workflow${
              workflowCount === 1 ? '' : 's'
            } attached. Archiving it will leave ${
              workflowCount === 1 ? 'it' : 'them'
            } unattached — ${
              workflowCount === 1 ? 'it' : 'they'
            } will stay in your workspace but won't run from this trigger anymore. Are you sure?`
          : undefined;
      return [
        // No Favorite entry: a trigger is machinery for running workflows, not
        // something anyone keeps a shortcut to. The workflow rows nested under
        // it carry the star instead.
        {
          label: 'Add workflow',
          icon: 'plus',
          onClick: () => {
            handleOpenMenu(trigger);
            handleAddWorkflowToTrigger(trigger);
          },
        },
        // No Share entry: a trigger is not shareable on its own, so this menu
        // takes the trio's two placements without the first.
        ResourceMenuItem.moveToFolder(() => {
          handleOpenMenu(trigger);
          void moveToFolderAction.execute({});
        }),
        ResourceMenuItem.archive(() => {
          handleOpenMenu(trigger);
          void archiveAction.execute(
            archiveConfirmSubtitle !== undefined
              ? { confirmSubtitle: archiveConfirmSubtitle }
              : {}
          );
        }),
      ];
    },
    [
      handleOpenMenu,
      handleAddWorkflowToTrigger,
      moveToFolderAction,
      archiveAction,
    ]
  );

  // The nested workflow rows in the grouped view are workflow resources, so
  // their kebab menu targets `ResourceType.Workflow` (distinct from the
  // trigger-level menu above, which targets `WorkflowTrigger`).
  const handleOpenWorkflowMenu = useCallback(
    (workflowId: string, name: string) =>
      handleOpenActionMenu({
        id: workflowId,
        name,
        resourceType: ResourceType.Workflow,
      }),
    [handleOpenActionMenu]
  );

  const getWorkflowRowActions = useCallback(
    (workflow: WorkflowRowRef): ItemAction[] => [
      favoriteMenuItem(ResourceType.Workflow, workflow.workflowId),
      {
        label: 'View runs',
        icon: 'history',
        onClick: () => {
          navigate(`/${org.slug}/workflows/${workflow.workflowId}/runs`);
        },
      },
      // Rename sits between Share and Move here, so this menu composes the
      // three entries itself rather than spreading the trio helper.
      ResourceMenuItem.share(() => {
        handleOpenWorkflowMenu(workflow.workflowId, workflow.name);
        void shareAction.execute({});
      }),
      ResourceMenuItem.rename(() => {
        handleOpenWorkflowMenu(workflow.workflowId, workflow.name);
        void renameWorkflowAction.execute({
          workflowId: workflow.workflowId,
          name: workflow.name,
        });
      }, 'Rename workflow'),
      ResourceMenuItem.moveToFolder(() => {
        handleOpenWorkflowMenu(workflow.workflowId, workflow.name);
        void moveToFolderAction.execute({});
      }),
      ResourceMenuItem.archive(() => {
        handleOpenWorkflowMenu(workflow.workflowId, workflow.name);
        void archiveAction.execute({});
      }),
    ],
    [
      favoriteMenuItem,
      navigate,
      org.slug,
      handleOpenWorkflowMenu,
      shareAction,
      renameWorkflowAction,
      moveToFolderAction,
      archiveAction,
    ]
  );

  // The grouped Workflows view is entered by workflow, so search must also match
  // workflow names — a trigger stays visible if any workflow bound to it
  // matches. `summaries[*].triggers` gives us each workflow's trigger ids, so we
  // can resolve matches without loading every trigger's workflow list.
  const workflowMatchTriggerIds = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query === '') {
      return null;
    }
    const ids = new Set<string>();
    for (const w of allWorkflows) {
      if (w.name.toLowerCase().includes(query)) {
        workflowSummaries
          .get(w.id)
          ?.triggers.forEach((t) => ids.add(t.triggerId));
      }
    }
    return ids;
  }, [search, allWorkflows, workflowSummaries]);

  // Calendar view bypasses ResourceListView's internal filter, so both the
  // health filter and the search are applied here for the calendar/accordion
  // content overrides.
  const filteredSortedTriggers = useMemo(() => {
    // Health first, and on its own pass. It used to be a clause inside the
    // search filter below, which returns early when the box is empty — so
    // picking a status did nothing at all unless you also typed something.
    const byHealth = triggers.filter((t) =>
      matchesHealthFilter(healthFilter, triggerSummaries.get(t.id))
    );
    const query = search.trim().toLowerCase();
    if (query === '') {
      return byHealth;
    }
    return byHealth.filter((t) => {
      // A dataset trigger is identified by the dataset it watches, so the
      // dataset's name has to be searchable even though it isn't on the row.
      const datasetName =
        t.datasetId !== null
          ? datasetNameById.get(t.datasetId)?.toLowerCase() ?? ''
          : '';
      return (
        filterTrigger(t, query) ||
        (datasetName !== '' && datasetName.includes(query)) ||
        (workflowMatchTriggerIds?.has(t.id) ?? false)
      );
    });
  }, [
    triggers,
    search,
    workflowMatchTriggerIds,
    datasetNameById,
    healthFilter,
    triggerSummaries,
  ]);

  // Unattached workflows are matched by name so the not-connected section (and
  // its banner) stays in sync with the search.
  const filteredUnattached = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query === '') {
      return unattached;
    }
    return unattached.filter((w) => w.name.toLowerCase().includes(query));
  }, [unattached, search]);

  // A search with no matches in any *visible* category should surface the empty
  // state rather than a blank accordion. Matches in a filtered-out category
  // (e.g. a webhook hit while only "Scheduled" is selected) don't count.
  const noSearchMatch = useMemo(() => {
    if (search.trim() === '') {
      return false;
    }
    // Match per category exactly the way the accordion groups them, so a hit in
    // a visible section always keeps the accordion on screen — and a hit in a
    // kind the accordion has no section for still reads as "no match".
    const matchesKind = (visible: boolean, kind: string): boolean =>
      visible && filteredSortedTriggers.some((t) => t.kind === kind);
    const scheduledMatch = matchesKind(visibleCategories.scheduled, 'cron');
    const webhookMatch = matchesKind(visibleCategories.webhook, 'webhook');
    const eventMatch = matchesKind(visibleCategories.event, 'event');
    const datasetMatch = matchesKind(visibleCategories.dataset, 'dataset');
    const standaloneMatch =
      visibleCategories.standalone && filteredUnattached.length > 0;
    return (
      !scheduledMatch &&
      !webhookMatch &&
      !eventMatch &&
      !datasetMatch &&
      !standaloneMatch
    );
  }, [search, visibleCategories, filteredSortedTriggers, filteredUnattached]);

  const renderTrigger = useCallback(
    (trigger: TriggerListItem) => (
      <>
        <div className="min-w-0 w-full @list-cols/list:flex-1 @list-cols/list:min-w-0">
          <div className="min-w-0">
            <Text.P className="font-medium text-slate-800 min-w-0 w-full truncate">
              {trigger.name}
            </Text.P>
            <p className="text-[11px] text-muted-foreground/50 truncate mt-0.5">
              {trigger.kind === 'dataset'
                ? [
                    trigger.datasetId !== null
                      ? datasetNameById.get(trigger.datasetId)
                      : undefined,
                    datasetCriterionLabel(trigger),
                  ]
                    .filter((part) => part !== undefined && part !== '')
                    .join(' · ')
                : humanReadableSchedule(trigger.schedule)}
              {trigger.agentTitle !== null ? ` · ${trigger.agentTitle}` : ''}
            </p>
          </div>
        </div>
        <ResourceListSecondaryCells>
          <div className="min-w-0 shrink-0 text-left @list-cols/list:w-[140px] @list-cols/list:pl-3" />
          <div className="min-w-0 shrink-0 text-left @list-cols/list:w-[170px] @list-cols/list:pl-3" />
          <div className="min-w-0 shrink-0 text-left @list-cols/list:w-[170px] @list-cols/list:pl-3">
            <span className={cn(RESOURCE_LIST_CELL_TEXT, 'block truncate')}>
              {trigger.kind === 'cron' && trigger.schedule !== ''
                ? formatNextRun(getNextRun(trigger.schedule))
                : 'Event-driven'}
            </span>
          </div>
        </ResourceListSecondaryCells>
      </>
    ),
    [datasetNameById]
  );

  /**
   * Each view gets its own box. Without `spacing` the group's items are
   * `rounded-none` with `border-l-0` and fuse into one segmented control; with it
   * they keep their own border and radius. Sized and rounded to the header's `+`
   * button (`size-8`, `rounded-sm`) so the controls on this band read as one set.
   */
  const viewToggle = (
    <ToggleGroup
      type="single"
      variant="outline"
      size="sm"
      spacing={1}
      value={viewMode}
      onValueChange={(v) => {
        if (v !== '') {
          handleViewModeChange(v as TriggersViewMode);
        }
      }}
      aria-label="View mode"
    >
      <ToggleGroupItem
        value="list"
        aria-label="List view"
        title="List view"
        className={VIEW_TOGGLE_ITEM_CLASS}
      >
        <List className="h-4 w-4" />
      </ToggleGroupItem>
      <ToggleGroupItem
        value="calendar"
        aria-label="Schedule view"
        title="Schedule view"
        className={VIEW_TOGGLE_ITEM_CLASS}
      >
        <CalendarDays className="h-4 w-4" />
      </ToggleGroupItem>
    </ToggleGroup>
  );

  // The Library's header band: source chips on the left, the view switch on the
  // right, then the column headers the rows line up under. The column headers
  // are rendered here (rather than by `ResourceListView`) because the grouped
  // accordion is a `contentOverride`, which suppresses the built-in ones — so
  // only add them when the override is actually on screen.
  // The chip band's narrow-screen form: one menu, on the title's row. See
  // `ListFilterMenu`.
  const categoryMenu = (
    <>
      <ListFilterMenu
        active={activeCategoryChip !== 'all'}
        groups={[
          {
            label: 'Trigger source',
            value: activeCategoryChip,
            options: [{ value: 'all', label: 'All' }, ...CATEGORY_CHIPS],
            onChange: handleCategoryChipChange,
          },
        ]}
      />
      <div className="shrink-0">{viewToggle}</div>
    </>
  );

  const headerExtras = (
    <>
      <div
        className={cn(
          'mb-6 flex flex-wrap items-center justify-between gap-3',
          // Gone on a phone — its two controls moved up to the title's row.
          // The column header below it stays put, so this cannot simply not be
          // rendered from the call site.
          isNarrow && 'hidden'
        )}
      >
        {/* "All" is the whole picture; the chips beyond it narrow to one
            source — the divider `FilterChipGroup` draws signals they're a
            different kind of view. */}
        <FilterChipGroup
          value={activeCategoryChip}
          onValueChange={handleCategoryChipChange}
          options={CATEGORY_CHIPS}
          ariaLabel="Filter workflows by trigger type"
        />
        <div className="shrink-0">{viewToggle}</div>
      </div>
      {viewMode === 'list' && triggersStatus === 'ready' ? (
        <AutomationsColumnHeader />
      ) : null}
    </>
  );

  return (
    <div className="h-full w-full">
      <ResourceListView
        title="Automations"
        // One knob for four metrics — title size, the header's top padding, and
        // the title row's own margin and padding — so list pages share a header
        // rhythm instead of each arriving at one through hand-tuned numbers.
        headerSize="compact"
        // Gutters belong to the page frame (see `contentClassName`), so drop the
        // default `px-8` rather than letting the two add up.
        containerClassName="px-0"
        subtitleOverride={summaryLine}
        noun="trigger"
        items={triggers}
        status={triggersStatus}
        filterFn={filterTrigger}
        renderItem={renderTrigger}
        icon={Clock}
        keyFn={(trigger) => trigger.id}
        onClickItem={handleClickTrigger}
        onFocus={handleFocusTriggers}
        itemActions={getTriggerActions}
        onOpenActionMenu={handleOpenMenu}
        emptyIcon={
          <Clock className="h-12 w-12 text-muted-foreground/50 mb-4" />
        }
        emptyText="No triggers yet"
        noMatchText="No triggers match your search"
        // Menu-item icons carry no `mr-*`: `DropdownMenuItem` already sets
        // `gap-2`, so a margin on top of it double-spaced the icon from its
        // label. Matches the Library's menu (PR #9140), which states the muted
        // colour explicitly even though the item's
        // `[&_svg:not([class*='text-'])]` rule would apply it anyway.
        /*
         * Both halves of the model, offered on the page that lists both. This
         * menu existed once before and was cut on the grounds that its two
         * options converge — the trigger wizard creates a *workflow* with a
         * trigger node (see `CreateStandaloneTriggerAction`) and opens its
         * builder — which is true, and still not the point. The standalone
         * wizard then moved to the Library's "New" menu, where triggers are
         * never *listed*: the thing you just made left no trace on the page that
         * offered to make it. Here it lands in the list you are looking at.
         *
         * Workflow leads, and stays the bare-button fallback: a trigger with
         * nothing attached can't do any work. Same order the empty state uses.
         */
        primaryAction={primaryAction}
        searchPlaceholder="Search..."
        searchQuery={search}
        onSearchQueryChange={handleSearchChange}
        /*
         * Status only. The list carries no sort control: the accordion
         * concatenates triggers by kind in a fixed order, so any sort could only
         * reorder rows *within* a source block — with a handful of triggers per
         * block that reads as a control that does nothing. The two orderings
         * worth having, last run and next run, are already columns.
         */
        filterOptions={(['failing', 'active', 'ready', 'idle'] as const).map(
          (health) => ({
            label: TRIGGER_HEALTH_LABEL[health],
            key: health,
            checked: healthFilter.has(health),
            onChange: () => toggleHealth(health),
          })
        )}
        columns={TRIGGER_COLUMNS}
        headerExtras={headerExtras}
        // Not the either/or the Library uses: `headerExtras` also carries the
        // column header, which stays put at every width, so the chip band
        // inside it hides itself rather than being dropped from here.
        toolbarExtras={isNarrow ? categoryMenu : undefined}
        contentOverride={
          viewMode === 'calendar' ? (
            <TriggersCalendar
              triggers={filteredSortedTriggers}
              onClickTrigger={handleClickTrigger}
            />
          ) : // Only override with the accordion once data is ready — while loading
          // we return undefined so ResourceListView shows its own skeleton instead
          // of an empty accordion with a misleading "0 triggers" count.
          // NOTE: like the calendar override, the accordion shows the first page
          // only (page size 100); paginating these grouped views is a follow-up.
          triggersStatus === 'ready' ? (
            noSearchMatch ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                No workflows or triggers match your search
              </div>
            ) : (
              <TriggersAccordionView
                triggers={filteredSortedTriggers}
                onNewTrigger={handleNewTrigger}
                onNewWorkflow={handleNewWorkflow}
                datasetNameById={datasetNameById}
                workflowSummaries={workflowSummaries}
                summaries={triggerSummaries}
                summariesLoaded={triggerSummariesLoaded}
                unattached={filteredUnattached}
                visibleCategories={visibleCategories}
                searchActive={search.trim() !== ''}
                onFocusTriggers={handleFocusTriggers}
                onArchiveTriggers={handleArchiveTriggers}
                // Same pair of calls the `…` menu's "Add workflow" makes:
                // select the trigger first so the action targets it, then open
                // the new-workflow flow seeded with it.
                onAddWorkflow={(trigger) => {
                  handleOpenMenu(trigger);
                  handleAddWorkflowToTrigger(trigger);
                }}
                workflowRowActions={getWorkflowRowActions}
                triggerActions={getTriggerActions}
              />
            )
          ) : undefined
        }
        hasMore={hasMore}
        loadPage={handleLoadPage}
        page={page}
        take={limit}
        isLoadingMore={false}
      />
    </div>
  );
};

/**
 * The unified Workflows page (triggers grouped by workflow). Mounted at
 * `/workflows`.
 */
export const WorkflowsTriggersPage: React.FC = () => {
  const { createModels, deps } = useListPageModelFactory({
    createModel: ({ client, urlAdapter, queryClient, orgId }) =>
      new TriggersListModel({
        orgId,
        queryClient,
        client,
        urlAdapter,
        initialSearchParams: new URLSearchParams(
          typeof window !== 'undefined' ? window.location.search : ''
        ),
      }),
  });

  return (
    // Matches the Library: a 1640px cap so very wide displays don't stretch rows
    // to an unreadable line length, and 48px gutters at `lg`. `ResourceListView`
    // contributes 32px of that itself, so only the remaining 16px is added here.
    <ListPageShell
      createModels={createModels}
      deps={deps}
      /*
       * The standard page frame for a wide resource list: one width cap, one set
       * of responsive gutters, one bottom margin. Gutters live out here rather
       * than inside `ResourceListView` — whose default `px-8` the
       * `containerClassName` below drops — so the two never stack, which is what
       * lets a `contentOverride` (the calendar) sit flush with the rows it
       * replaces.
       */
      contentClassName="max-w-[1640px] px-5 pb-16 pt-1 sm:px-8 sm:pt-5 lg:px-12"
    >
      <TriggersListContent />
    </ListPageShell>
  );
};

/**
 * `/triggers` route. Redirects to the unified `/workflows` page, keeping old
 * `/triggers` bookmarks working.
 */
export const TriggersPage: React.FC = () => {
  const org = useWhoami((s) => s.org);
  const location = useLocation();

  return <Navigate to={`/${org.slug}/workflows${location.search}`} replace />;
};
